import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface ColorWithContrast {
  color: string;
  contrast: string;
}

export interface ColorPalette {
  [key: string]: ColorWithContrast;
}

interface PaletteState {
  branding: ColorPalette;
  cta: ColorPalette;
  brandingBaseShade: number;
  ctaBaseShade: number;
  showBrandingWarning: boolean;
  showCtaWarning: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly lightThreshold = 0.75;
  private readonly darkThreshold = 0.25;

  private brandingColor = new BehaviorSubject<string>('#0063AB');
  private ctaColor = new BehaviorSubject<string>('#FA9401');
  private paletteState = new BehaviorSubject<PaletteState>({
    branding: {},
    cta: {},
    brandingBaseShade: 500,
    ctaBaseShade: 500,
    showBrandingWarning: false,
    showCtaWarning: false
  });

  // Exposed subjects as observables
  paletteState$ = this.paletteState.asObservable();
  brandingColor$ = this.brandingColor.asObservable();
  ctaColor$ = this.ctaColor.asObservable();

  constructor() {
    this.generateInitialPalette();
  }

  private generateInitialPalette() {
    this.updatePalette();
  }

  updateBrandingColor(color: string) {
    this.brandingColor.next(color);
    this.updatePalette();
  }

  updateCtaColor(color: string) {
    this.ctaColor.next(color);
    this.updatePalette();
  }

  resetTheme() {
    this.brandingColor.next('#0063AB');
    this.ctaColor.next('#FA9401');
    this.updatePalette();
  }

  updatePalette() {
    const brandingColor = this.brandingColor.value;
    const ctaColor = this.ctaColor.value;

    // Generate branding palette
    const {
      palette: brandingPalette,
      baseShade: brandingBaseShade,
      showWarning: showBrandingWarning
    } = this.generatePaletteWithBaseShade(brandingColor);

    // Generate CTA palette
    const {
      palette: ctaPalette,
      baseShade: ctaBaseShade,
      showWarning: showCtaWarning
    } = this.generatePaletteWithBaseShade(ctaColor);

    this.paletteState.next({
      branding: brandingPalette,
      cta: ctaPalette,
      brandingBaseShade,
      ctaBaseShade,
      showBrandingWarning,
      showCtaWarning
    });
  }

  private generatePaletteWithBaseShade(hex: string): {
    palette: ColorPalette;
    baseShade: number;
    showWarning: boolean;
  } {
    const { h, s, l } = this.hexToHsl(hex);
    let baseShade = this.brandingColor.value === hex ? 500 : 500;
    let showWarning = false;

    // Determine base shade based on lightness
    if (l >= this.lightThreshold) {
      baseShade = 200;
      showWarning = true;
    } else if (l <= this.darkThreshold) {
      baseShade = 800;
      showWarning = true;
    }

    const palette = this.generatePaletteFromBaseColor(hex, baseShade);

    return { palette, baseShade, showWarning };
  }

  private generatePaletteFromBaseColor(hex: string, baseShade: number): ColorPalette {
    const { h, s, l } = this.hexToHsl(hex);
    const palette: ColorPalette = {};

    // Define lightness adjustments for each shade
    const shadeMap = {
      10: 0.995, // Almost pure white with barely noticeable color
      50: 0.95, // Much lighter
      100: 0.93, // Lighter
      200: 0.85, // Adjusted
      300: 0.75, // Kept same
      400: 0.65, // Slightly darker
      500: 0.55, // Adjusted
      600: 0.45, // Darker
      700: 0.35, // Kept same
      800: 0.25, // Slightly lighter
      900: 0.15 // Slightly lighter
    } as const;

    type ShadeKey = keyof typeof shadeMap;

    // Adjust the lightness calculations based on where the base color is placed
    const baseL = shadeMap[baseShade as ShadeKey];

    // Generate color for each shade
    Object.keys(shadeMap).forEach(shade => {
      const shadeNum = parseInt(shade);
      const targetL = shadeMap[shadeNum as ShadeKey];

      // If this shade IS the base shade, use the original color
      if (shadeNum === baseShade) {
        palette[shade] = { color: hex, contrast: this.getTextColor(hex) };
        return;
      }

      // For other shades, calculate appropriate lightness
      let adjustedL;
      let adjustedS = s;

      if (shadeNum < baseShade) {
        // Lighter shades - more gradual transition
        const range = l - 0.96;
        const position = (baseL - targetL) / (baseL - 0.96);
        adjustedL = l - range * position;
        adjustedL = Math.min(0.96, adjustedL);

        // Reduce saturation more gradually for lighter shades
        if (shadeNum === 10) {
          // For shade 10, make it very light but maintain some color
          adjustedS = Math.max(0, s * 0.9); // Keep 30% of original saturation
          adjustedL = 0.987; // Very light but not pure white
        } else if (targetL > 0.8) {
          adjustedS = Math.max(0, s * 0.85);
        } else if (targetL > 0.7) {
          adjustedS = Math.max(0, s * 0.9);
        }
      } else {
        // Darker shades - maintain richness
        const range = l - 0.15;
        const position = (targetL - baseL) / (0.15 - baseL);
        adjustedL = l - range * position;
        adjustedL = Math.max(0.15, adjustedL);

        // Maintain or slightly increase saturation for darker shades
        if (targetL < 0.3) {
          adjustedS = Math.min(1, s * 1.1);
        }
      }

      palette[shade] = {
        color: this.hslToHex(h, adjustedS, adjustedL),
        contrast: this.getTextColor(this.hslToHex(h, adjustedS, adjustedL))
      };
    });

    return palette;
  }

  // Color conversion utilities
  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    hex = hex.replace(/^#/, '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h: number;
    let s: number;
    const l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }

    return { h, s, l };
  }

  private hslToHex(h: number, s: number, l: number): string {
    let r: number, g: number, b: number;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (x: number) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  calculateContrast(color1: string, color2: string): number {
    const getLuminance = (hex: string) => {
      hex = hex.replace(/^#/, '');
      const r = parseInt(hex.substr(0, 2), 16) / 255;
      const g = parseInt(hex.substr(2, 2), 16) / 255;
      const b = parseInt(hex.substr(4, 2), 16) / 255;

      const R = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
      const G = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
      const B = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

      return 0.2126 * R + 0.7152 * G + 0.0722 * B;
    };

    const L1 = getLuminance(color1);
    const L2 = getLuminance(color2);

    const ratio = L1 > L2 ? (L1 + 0.05) / (L2 + 0.05) : (L2 + 0.05) / (L1 + 0.05);
    return Math.round(ratio * 100) / 100;
  }

  meetsWcagAA(hex: string, background: string = '#ffffff'): boolean {
    return this.calculateContrast(hex, background) >= 4.5;
  }

  getTextColor(bgColor: string): string {
    const contrastWithBlack = this.calculateContrast(bgColor, '#000000');
    const contrastWithWhite = this.calculateContrast(bgColor, '#ffffff');
    return contrastWithBlack > contrastWithWhite ? '#000000' : '#ffffff';
  }
}
